import React, { useState } from 'react';
import { User } from '../../services/authService';
import { passwordService, PasswordEntry } from '../../services/passwordService';
import './Password.css';

interface PasswordListProps {
  user: User | null;
  passwords: PasswordEntry[];
  onEdit: (entry: PasswordEntry) => void;
  onDelete: (id: string) => void;
  refreshList: () => void;
}

const PasswordList: React.FC<PasswordListProps> = ({ passwords, onEdit, onDelete, refreshList }) => {
  const [visiblePasswords, setVisiblePasswords] = useState<Map<string, string>>(new Map());
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [loadingPasswords, setLoadingPasswords] = useState<Set<string>>(new Set());

  const togglePasswordVisibility = async (id: string) => {
    // If password is already visible, hide it
    if (visiblePasswords.has(id)) {
      setVisiblePasswords(prev => {
        const newMap = new Map(prev);
        newMap.delete(id);
        return newMap;
      });
      return;
    }

    // If password is not loaded, fetch it
    if (!visiblePasswords.has(id)) {
      try {
        setLoadingPasswords(prev => new Set(prev).add(id));
        const response = await passwordService.getPasswordById(id);
        if (response.success && response.password && response.password.password) {
          setVisiblePasswords(prev => {
            const newMap = new Map(prev);
            newMap.set(id, response.password!.password!);
            return newMap;
          });
        }
      } catch (error) {
        console.error('Error fetching password:', error);
        alert('Failed to load password');
      } finally {
        setLoadingPasswords(prev => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
      }
    }
  };

  const handleCopyPassword = async (id: string) => {
    try {
      // If password is already visible, use it
      let password = visiblePasswords.get(id);
      
      // Otherwise, fetch it
      if (!password) {
        const response = await passwordService.getPasswordById(id);
        if (response.success && response.password && response.password.password) {
          password = response.password.password;
          // Cache it for viewing
          setVisiblePasswords(prev => {
            const newMap = new Map(prev);
            newMap.set(id, password!);
            return newMap;
          });
        } else {
          throw new Error('Failed to fetch password');
        }
      }
      
      if (!password) {
        throw new Error('Password is not available');
      }
      
      await navigator.clipboard.writeText(password);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error('Failed to copy password:', error);
      alert('Failed to copy password to clipboard');
    }
  };

  const maskPassword = (): string => {
    return '•'.repeat(20);
  };

  if (passwords.length === 0) {
    return <div className="password-empty">No passwords found</div>;
  }

  return (
    <div className="password-list">
      {passwords.map((entry) => {
        const isVisible = visiblePasswords.has(entry.id);
        const isCopied = copiedId === entry.id;
        const isLoading = loadingPasswords.has(entry.id);
        const password = visiblePasswords.get(entry.id);
        
        return (
          <div key={entry.id} className="password-item">
            <div className="password-item-header">
              <div className="password-item-info">
                <h3 className="password-item-account">{entry.account_name}</h3>
                {entry.website_url && (
                  <a 
                    href={entry.website_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="password-item-url"
                  >
                    {entry.website_url}
                  </a>
                )}
                {entry.username && (
                  <div className="password-item-username">{entry.username}</div>
                )}
              </div>
              <div className="password-item-actions">
                <button
                  className="password-copy-btn"
                  onClick={() => handleCopyPassword(entry.id)}
                  title="Copy password"
                  disabled={isLoading}
                >
                  {isCopied ? '✓' : '📋'}
                </button>
                <button
                  className="password-show-btn"
                  onClick={() => togglePasswordVisibility(entry.id)}
                  title={isVisible ? 'Hide password' : 'Show password'}
                  disabled={isLoading}
                >
                  {isLoading ? '⏳' : isVisible ? '👁️' : '👁️‍🗨️'}
                </button>
                <button
                  className="password-edit-btn"
                  onClick={() => onEdit(entry)}
                  title="Edit"
                >
                  ✏️
                </button>
                <button
                  className="password-delete-btn"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this password?')) {
                      onDelete(entry.id);
                    }
                  }}
                  title="Delete"
                >
                  🗑️
                </button>
              </div>
            </div>
            
            <div className="password-item-password">
              <span className={`password-display ${isVisible ? 'password-visible' : 'password-masked'}`}>
                {isLoading ? 'Loading...' : isVisible && password ? password : maskPassword()}
              </span>
            </div>
            
            {entry.notes && (
              <div className="password-item-notes">{entry.notes}</div>
            )}
            
            <div className="password-item-meta">
              Created: {new Date(entry.created_at).toLocaleDateString()}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PasswordList;

